import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { Stack, Typography, Tab, Tooltip } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { TabContext, TabList } from '@mui/lab';
import { carrierOptions, stateCodeArray } from '../../constants';
import MultipleSelect from '../../components/styledComponents/MultipleSelect';
import { SelectedItems } from '../../doc-search/config';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import { NormalizedStatus_V2 } from '../../components/DocEditContent/constants';
import { StatusKey } from '../../components/DocEditContent/partials/ApplicableStates';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';

type PropsType = { state_filings: StateFiling[]; form_type_of_business: string };

type StateFiling = {
  carrier: string;
  effective_date: string | null;
  expiration_date: string | null;
  state_filed: string;
  state_status: StatusKey;
};

type StateFilter = Partial<Record<SearchFilterId, Option[]>>;

const STATUS_CONFIG: Record<
  NormalizedStatus_V2,
  {
    textColor: string;
    fontWeight: 'normal' | 'bold';
    tooltip: string;
    Icon: typeof CheckCircleOutlinedIcon;
    iconColor: 'success' | 'error' | 'warning';
  }
> = {
  [NormalizedStatus_V2.AVAILABLE]: {
    textColor: 'black',
    fontWeight: 'bold',
    tooltip: 'Available for Use',
    Icon: CheckCircleOutlinedIcon,
    iconColor: 'success',
  },
  [NormalizedStatus_V2.NOT_AVAILABLE]: {
    textColor: 'gray',
    fontWeight: 'normal',
    tooltip: 'Not Available',
    Icon: CancelOutlinedIcon,
    iconColor: 'error',
  },
  [NormalizedStatus_V2.PENDING]: {
    textColor: 'black',
    fontWeight: 'normal',
    tooltip: 'Pending Availability - Refer to Effective Date',
    Icon: ErrorOutlineOutlinedIcon,
    iconColor: 'warning',
  },
};

export const getDateString = (date: string | null | undefined) =>
  date ? moment.utc(date).format('MM/DD/YYYY') : 'N/A';

const ApplicableStates = ({ state_filings, form_type_of_business }: PropsType) => {
  const [tabValue, setTabValue] = useState<string>('0');
  const [filters, setFilters] = useState<StateFilter>({});

  const containerRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const carriers = useMemo(() => {
    return Array.from(
      state_filings.reduce((acc, cur) => {
        if (!acc.has(cur.carrier)) {
          acc.add(cur.carrier);
        }
        return acc;
      }, new Set())
    );
  }, [state_filings]);

  const carrierHeader = useMemo(
    () =>
      `${
        carrierOptions.find((c) => c.value === carriers[parseInt(tabValue)])?.label.split(' - ')[1]
      } - ${form_type_of_business}`,
    [tabValue, carriers, form_type_of_business]
  );
  const carrierName = useMemo(() => carriers[parseInt(tabValue)], [tabValue, carriers]);
  const states = useMemo(() => {
    if (filters.state_filed && filters.state_filed.length > 0) {
      return filters.state_filed.map((s) => s.label);
    } else {
      return stateCodeArray.map((s) => s.key);
    }
  }, [filters.state_filed]);

  useLayoutEffect(() => {
    const el = containerRef.current?.querySelector('.MuiTabs-scroller') as HTMLDivElement | null;
    if (!el) return;

    const checkOverflow = () => {
      setIsOverflowing(el.scrollWidth > el.clientWidth);
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [carriers.length]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  const getStatus = (date: string | undefined | null): NormalizedStatus_V2 => {
    if (!date) return NormalizedStatus_V2.NOT_AVAILABLE;

    return moment(date).isSameOrBefore(moment(), 'day')
      ? NormalizedStatus_V2.AVAILABLE
      : NormalizedStatus_V2.PENDING;
  };

  const handleSelect = (id: SearchFilterId, items: SelectedItems) => {
    setFilters((prevState) => {
      const newFilters = { ...prevState };
      newFilters[id] = items as any;

      return newFilters;
    });
  };

  return (
    <Stack spacing={1.5}>
      <Stack>
        <Typography display={'flex'} justifyContent={'center'}>
          <strong>State Availability Overview</strong>
        </Typography>
        <TabContext value={tabValue}>
          <TabList
            ref={containerRef}
            variant="scrollable"
            onChange={handleTabChange}
            sx={{
              minHeight: 0,
              '& .MuiTabs-flexContainer': {
                ...(!isOverflowing && { justifyContent: 'center' }),
              },
              '& .MuiTab-root': {
                minHeight: 0,
                padding: '4px 8px',
              },
              '& .MuiTab-root.Mui-selected': {
                minHeight: 0,
              },
            }}
          >
            {carriers.map((carrier, index) => (
              <Tab
                key={index}
                label={`${carrier} - ${form_type_of_business}`}
                value={index.toString()}
                sx={{ textTransform: 'capitalize', color: '#041c2c' }}
              />
            ))}
          </TabList>
        </TabContext>
      </Stack>
      <Stack direction={'row'} spacing={2} justifyContent={'center'}>
        <Stack width={'40%'}>
          <MultipleSelect
            label="State"
            options={stateCodeArray.map((x) => ({ label: x.key, value: x.key }))}
            name="state_filed"
            selectedValue={filters?.state_filed ?? ([] as any)}
            dispatchFilter={handleSelect}
          />
        </Stack>
        <Stack width={'40%'}>
          <MultipleSelect
            label="Status"
            options={Object.values(NormalizedStatus_V2).map((option) => ({
              value: option,
              label: option,
            }))}
            name="form_status"
            selectedValue={filters?.form_status ?? ([] as any)}
            dispatchFilter={handleSelect}
          />
        </Stack>
      </Stack>
      <TableContainer sx={{ height: 300, overscrollBehavior: 'contain' }} component={Paper}>
        <Table sx={{ minWidth: 0.5 }} stickyHeader size="small" aria-label="a dense table">
          <TableHead>
            <TableRow>
              <TableCell
                colSpan={4}
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 3,
                  fontWeight: 700,
                }}
              >
                {carrierHeader}
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell width={'25%'} sx={{ top: 37, zIndex: 2, fontWeight: 600 }}>
                State
              </TableCell>
              <TableCell width={'25%'} sx={{ top: 37, zIndex: 2, fontWeight: 600 }}>
                Status
              </TableCell>
              <TableCell width={'25%'} sx={{ top: 37, zIndex: 2, fontWeight: 600 }}>
                Effective
              </TableCell>
              <TableCell width={'25%'} align="right" sx={{ top: 37, zIndex: 2, fontWeight: 600 }}>
                Expiry
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {states.map((state) => {
              const found = state_filings.find(
                (s) => s.carrier === carrierName && s.state_filed === state
              );

              const status = getStatus(found?.effective_date);
              const { textColor, fontWeight, tooltip, Icon, iconColor } = STATUS_CONFIG[status];

              const style = { color: textColor, fontWeight };

              const showRow =
                !filters.form_status?.length || filters.form_status.some((f) => f.value === status);

              if (!showRow) return null;

              return (
                <TableRow key={state} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component="th" scope="row" sx={style}>
                    {state}
                  </TableCell>

                  <TableCell>
                    <Tooltip title={tooltip}>
                      <Icon color={iconColor} sx={{ ml: '6px' }} />
                    </Tooltip>
                  </TableCell>

                  <TableCell sx={style}>{getDateString(found?.effective_date)}</TableCell>

                  <TableCell align="right" sx={style}>
                    {getDateString(found?.expiration_date)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};

export default ApplicableStates;
